/* eslint-disable camelcase */
import React, { FC, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/react-hooks';
import { CourseProps } from 'Jsx/Shared/CourseCardV3/types';

import mixpanel from 'Jsx/Utils/mixpanelTracking';

import GET_CUSTOM_BUNDLE_QUERY from './graphql/GetCustomBundleMutation.graphql';
import ADD_COURSE_IN_CUSTOM_BUNDLE_MUTATION from './graphql/AddCourseInCustomBundleMutation.graphql';
import REMOVE_COURSE_FROM_CUSTOM_BUNDLE_MUTATION from './graphql/RemoveCourseFromCustomBundle.graphql';

import {
  GetCustomBundleMutation,
  GetCustomBundleMutationVariables,
} from './graphql/types/GetCustomBundleMutation';

import {
  AddCourseInCustomBundleMutation,
  AddCourseInCustomBundleMutationVariables,
} from './graphql/types/AddCourseInCustomBundleMutation';
import {
  RemoveCourseFromCustomBundle,
  RemoveCourseFromCustomBundleVariables,
} from './graphql/types/RemoveCourseFromCustomBundle';

interface State {
  bundleInfo: {
    slug: string;
    size: number;
    prices: {
      price: number;
      currencySymbol: string;
    };
    formatedPrice: string;
    ctaButtonText?: string;
  };
  coursesInBundle: CourseProps[];
  isCoursesBundleComplete: boolean;
  toggleCourseInBundle(course: CourseProps): void;
  addCourseToCustomBundle(course: CourseProps): void;
  hasInitialized: boolean;
  initialize(options: {
    slug: string;
    size: number;
    prices: { price: number; currencySymbol: string };
    ctaButtonText?: string;
  }): void;
  customBundle: CustomBundle;
  cleanCustomBundle(): void;
  addAllCoursesInCustomBundle: (ids: number[], courses: CourseProps[]) => void;
}

interface CustomBundle {
  originalId: number | null;
  redirectUrl: string;
  isCompleted: boolean;
  loading: boolean;
}

export const BundleContext = React.createContext<State | undefined>(undefined);

interface CoursesInBundleProviderProps {
  campaignSlug?: string;
  ctaButtonText?: string;
}

const CoursesInBundleProvider: FC<CoursesInBundleProviderProps> = ({
  campaignSlug = '',
  children,
}) => {
  const [bundleInfo, setBundleInfo] = React.useState<{
    slug: string;
    size: number;
    prices: { price: number; currencySymbol: string };
    formatedPrice: string;
    ctaButtonText?: string;
  }>({
    slug: '',
    size: 4,
    prices: { price: 0, currencySymbol: 'US$' },
    formatedPrice: '',
    ctaButtonText: undefined,
  });
  const [hasInitialized, setInitialized] = React.useState(false);

  const [customBundle, setCustomBundle] = React.useState<CustomBundle>({
    originalId: 0,
    redirectUrl: '',
    isCompleted: false,
    loading: true,
  });

  const [coursesInBundle, setCoursesInBundle] = React.useState<CourseProps[]>(
    [],
  );

  const [getBundleData] = useLazyQuery<
    GetCustomBundleMutation,
    GetCustomBundleMutationVariables
  >(GET_CUSTOM_BUNDLE_QUERY, {
    onCompleted: response => {
      if (response?.customBundle) {
        const {
          isActive,
          originalId,
          courses,
          redirectUrl,
          page,
          isCompleted,
        } = response.customBundle;

        setCustomBundle({
          originalId,
          redirectUrl: redirectUrl || '',
          isCompleted: !!isCompleted,
          loading: false,
        });
        if (isActive) {
          if ((courses?.length || 0) > 0) {
            setCoursesInBundle(courses);
          }

          if (page?.prices) {
            setBundleInfo({
              prices: page.prices,
              formatedPrice:
                page.prices.currencySymbol +
                page.prices.price?.toLocaleString(),
              size: page.customBundleItems || 0,
              slug: campaignSlug!,
            });
          }

          setInitialized(true);
        }
      }
    },
  });
  const [removeCustomBundleItemMutation] = useMutation<
    RemoveCourseFromCustomBundle,
    RemoveCourseFromCustomBundleVariables
  >(REMOVE_COURSE_FROM_CUSTOM_BUNDLE_MUTATION);
  const [addedCustomBundleItem] = useMutation<
    AddCourseInCustomBundleMutation,
    AddCourseInCustomBundleMutationVariables
  >(ADD_COURSE_IN_CUSTOM_BUNDLE_MUTATION);

  const checkIfItemIsInPack = (course: CourseProps) => {
    return Boolean(
      coursesInBundle.find(el => el.originalId === course.originalId),
    );
  };
  const addCourseToCustomBundle = (course: CourseProps) => {
    setCoursesInBundle(prev => [...prev, course]);
    setCustomBundle(prev => ({ ...prev, loading: true }));
    addedCustomBundleItem({
      variables: {
        pageSlug: campaignSlug,
        courseIds: [course.originalId],
      },
    }).then(({ data }) => {
      mixpanel.track('Add course to bundle', {});

      if (
        data?.addCustomBundleItem?.success &&
        data.addCustomBundleItem.customBundle
      ) {
        const {
          redirectUrl,
          customBundle: { originalId, courses, isCompleted },
        } = data.addCustomBundleItem;
        const addedCourse = courses?.find(
          c => c?.originalId === course.originalId,
        );

        if (addedCourse) {
          setCoursesInBundle(prev =>
            prev.map(c =>
              c.originalId === addedCourse.originalId
                ? { ...c, promoVideoHashedId: addedCourse.promoVideoHashedId }
                : c,
            ),
          );
        }

        setCustomBundle({
          originalId,
          redirectUrl: redirectUrl || '',
          isCompleted: !!isCompleted,
          loading: false,
        });
      }
    });
  };
  const removeCourseFromPack = (course: CourseProps) => {
    if (customBundle?.originalId && course?.originalId) {
      setCustomBundle(prev => ({ ...prev, loading: true }));
      removeCustomBundleItemMutation({
        variables: {
          customBundleId: customBundle.originalId.toString(),
          courseIds: [course.originalId],
        },
      }).then(({ data }) => {
        if (data?.removeCustomBundleItem?.success) {
          setCoursesInBundle(
            coursesInBundle.filter(el => el.originalId !== course.originalId),
          );
          setCustomBundle({
            ...customBundle,
            redirectUrl: '',
            isCompleted: false,
            loading: false,
          });
        }
      });
    }
  };
  const toggleCourseInBundle = (course: CourseProps) => {
    if (checkIfItemIsInPack(course)) {
      removeCourseFromPack(course);
    } else {
      addCourseToCustomBundle(course);
    }
  };
  const cleanCustomBundle = () => {
    if (coursesInBundle?.length > 0 && customBundle?.originalId) {
      setCustomBundle(prev => ({ ...prev, loading: true }));
      let courseIds: number[] = [];

      for (let i = 0; i < coursesInBundle.length; i += 1) {
        courseIds = [...courseIds, coursesInBundle[i].originalId];
      }
      removeCustomBundleItemMutation({
        variables: {
          customBundleId: customBundle.originalId.toString(),
          courseIds,
        },
      }).then(({ data }) => {
        if (data?.removeCustomBundleItem?.success) {
          setCoursesInBundle([]);
          setCustomBundle({
            ...customBundle,
            redirectUrl: '',
            isCompleted: false,
            loading: false,
          });
          mixpanel.track('Clear custom bundle', {});
        }
      });
    }
  };
  const addAllCoursesInCustomBundle = (
    courseIds: number[],
    courses: CourseProps[],
  ) => {
    setCustomBundle(prev => ({ ...prev, loading: true }));
    addedCustomBundleItem({
      variables: {
        pageSlug: campaignSlug,
        courseIds,
      },
    }).then(({ data }) => {
      if (
        data?.addCustomBundleItem?.success &&
        data.addCustomBundleItem.customBundle
      ) {
        const {
          redirectUrl,
          customBundle: { originalId, isCompleted },
        } = data.addCustomBundleItem;

        setCustomBundle({
          originalId,
          redirectUrl: redirectUrl || '',
          isCompleted: !!isCompleted,
          loading: false,
        });
        setCoursesInBundle(courses);
      }
    });
  };

  const initialize = ({ slug, size, prices, ctaButtonText }) => {
    setBundleInfo({
      slug,
      size,
      prices,
      formatedPrice: prices.currencySymbol + prices.price.toLocaleString(),
      ctaButtonText,
    });
    setInitialized(true);
  };

  useEffect(() => {
    if (campaignSlug) {
      getBundleData({
        variables: {
          pageSlug: campaignSlug,
        },
      });
    }
  }, [campaignSlug]);

  return (
    <BundleContext.Provider
      value={{
        coursesInBundle,
        toggleCourseInBundle,
        bundleInfo,
        hasInitialized,
        initialize,
        isCoursesBundleComplete: coursesInBundle.length === bundleInfo.size,
        customBundle,
        cleanCustomBundle,
        addCourseToCustomBundle,
        addAllCoursesInCustomBundle,
      }}
    >
      {children}
    </BundleContext.Provider>
  );
};

export const useCoursesInBundleState = () => {
  const context = React.useContext(BundleContext);

  if (context === undefined) {
    throw new Error(
      'useCoursesInBundleState must be used within a CoursesInBundleProvider',
    );
  }

  return context;
};

export default CoursesInBundleProvider;
